import { AnimationEvent } from '@angular/animations';
import { CdkPortal, TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Injector,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { tiimeTooltipAnimations } from './tooltip-animation';

@Component({
  selector: 'tiime-tooltip',
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [tiimeTooltipAnimations.tooltipState],
  standalone: false
})
export class TooltipComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly injector = inject(Injector);

  content: TemplateRef<any>;
  cdkPortal: CdkPortal;
  visibility = 'initial';
  tooltipClass: string | string[] | Set<string> | Record<string, any> | null;

  /** Subject for notifying that the tooltip has been hidden from the view */
  private readonly onHide = new Subject<void>();
  private showTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private hideTimeoutId: ReturnType<typeof setTimeout> | undefined;

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
  ngOnInit() {
    this.cdkPortal = new TemplatePortal(this.content, this.viewContainerRef, this.injector);
  }

  show(delay: number): void {
    // Cancel the delayed hide if it is scheduled
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
    }
    this.showTimeoutId = setTimeout(() => {
      this.visibility = 'visible';
      this.showTimeoutId = undefined;
      this.markForCheck();
    }, delay);
  }

  hide(delay: number): void {
    // Cancel the delayed show if it is scheduled
    if (this.showTimeoutId) {
      clearTimeout(this.showTimeoutId);
    }
    this.hideTimeoutId = setTimeout(() => {
      this.visibility = 'hidden';
      this.hideTimeoutId = undefined;
      this.markForCheck();
    }, delay);
  }

  afterHidden(): Observable<void> {
    return this.onHide.asObservable();
  }

  markForCheck(): void {
    this.cdr.markForCheck();
  }

  animationDone(event: AnimationEvent): void {
    const toState = event.toState;
    if (toState === 'hidden') {
      this.onHide.next();
    }
  }
}
